import React, { useEffect, useState, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { getVeiculos } from '../services/api';

const GOOGLE_MAPS_KEY = 'AIzaSyC_ZmKM-BeaAygfsk23bBCSw5oqH8VX13g';

const mapContainerStyle = { width: '100%', height: '100vh' };
const centroInicial = { lat: -15.7801, lng: -47.9292 };

// Só entra no enquadramento automático posição plausível (dentro da América do Sul).
// Evita que uma coordenada com defeito (0,0, polo, etc.) faça o mapa mostrar o mundo inteiro.
const posicaoParaEnquadrar = (v) => {
    const lat = Number(v.latitude), lng = Number(v.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (v.gps_valido === false) return false;
    return lat > -56 && lat < 13 && lng > -82 && lng < -34;
};

const mapStyles = {
    normal: [],
    satelite: 'satellite',
};

function formatarData(data) {
    if (!data) return 'Sem dados';
    return new Date(data).toLocaleString('pt-BR');
}

// Retorna a ignição efetiva: usa ignicao_atual (evento direto) se for mais recente que a última posição GPS
function ignicaoEfetiva(v) {
    if (v.ignicao_sem_gps_em && (!v.recebido_em || new Date(v.ignicao_sem_gps_em) > new Date(v.recebido_em))) {
        return { ignicao: v.ignicao_atual, semGps: true };
    }
    return { ignicao: v.ignicao, semGps: false };
}

function statusVeiculo(v) {
    const { ignicao, semGps } = ignicaoEfetiva(v);
    const temPosicao = !!v.recebido_em;
    const diffGps = temPosicao ? (Date.now() - new Date(v.recebido_em)) / 1000 / 60 : 999;

    if (!temPosicao && v.ignicao_sem_gps_em === null) return { label: 'Sem comunicação', cor: '#888888' };
    if (diffGps > 30 && !semGps) return { label: 'Offline', cor: '#e74c3c' };
    if (!ignicao) return { label: 'Desligado', cor: '#e74c3c' };
    if (semGps) return { label: 'Ig. Ligada · Sem GPS', cor: '#ff9500' };
    if (!v.gps_valido) return { label: 'Sem sinal GPS', cor: '#e67e22' };
    if (v.velocidade > 0) return { label: 'Em movimento', cor: '#27ae60' };
    return { label: 'Parado', cor: '#2980b9' };
}

function corIcone(v) {
    const { ignicao, semGps } = ignicaoEfetiva(v);
    if (!v.recebido_em && !v.ignicao_sem_gps_em) return '#888888';
    const diffGps = v.recebido_em ? (Date.now() - new Date(v.recebido_em)) / 1000 / 60 : 999;
    if (diffGps > 30 && !semGps) return '#e74c3c';
    if (!ignicao) return '#e74c3c';
    if (semGps) return '#ff9500';
    if ((v.velocidade || 0) > 0) return '#2980b9';
    return '#27ae60';
}

function iconeVeiculo(v) {
    const cor = corIcone(v);
    const label = (v.nome || v.placa || '').toUpperCase();
    const az = v.azimute || 0;

    // Dimensões: triângulo 40x40 + label abaixo
    const charW = 7;
    const labelW = Math.max(label.length * charW + 16, 50);
    const labelH = 23;
    const sz = 40; // tamanho do triângulo
    const totalW = Math.max(sz, labelW);
    const totalH = labelH + 4 + sz;
    const cx = totalW / 2;
    const id = 'g' + (v.id || Math.random().toString(36).slice(2, 7));

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalW} ${totalH}" width="${totalW * 2}" height="${totalH * 2}">
        <defs>
            <filter id="${id}sh" x="-40%" y="-40%" width="180%" height="180%">
                <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="rgba(0,0,0,0.45)"/>
            </filter>
            <filter id="${id}ls" x="-15%" y="-25%" width="130%" height="150%">
                <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.35)"/>
            </filter>
            <linearGradient id="${id}gl" x1="0.3" y1="0" x2="0.7" y2="1">
                <stop offset="0%" stop-color="rgba(255,255,255,0.35)"/>
                <stop offset="100%" stop-color="rgba(0,0,0,0.15)"/>
            </linearGradient>
        </defs>

        <!-- ── LABEL (topo) ── -->
        <rect
            x="${cx - labelW / 2}" y="0"
            width="${labelW}" height="${labelH}" rx="5"
            fill="${cor}"
            filter="url(#${id}ls)"
        />
        <text
            x="${cx}" y="${16}"
            text-anchor="middle"
            font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            font-size="11" font-weight="400" letter-spacing="0.8"
            fill="white"
        >${label}</text>

        <!-- ── TRIÂNGULO DIRECIONAL ── rotacionado pelo azimute -->
        <g transform="translate(0, ${labelH + 4}) rotate(${az}, ${cx}, ${sz / 2})">
            <polygon points="${cx},${2} ${cx - 16},${sz - 4} ${cx},${sz - 10} ${cx + 16},${sz - 4}"
                fill="${cor}" filter="url(#${id}sh)"/>
            <polygon points="${cx},${2} ${cx - 16},${sz - 4} ${cx},${sz - 10} ${cx + 16},${sz - 4}"
                fill="${cor}"/>
            <polygon points="${cx},${2} ${cx - 16},${sz - 4} ${cx},${sz - 10} ${cx + 16},${sz - 4}"
                fill="url(#${id}gl)"/>
            <line x1="${cx}" y1="${6}" x2="${cx}" y2="${sz - 11}"
                stroke="rgba(255,255,255,0.5)" stroke-width="1.5" stroke-linecap="round"/>
            <line x1="${cx - 10}" y1="${sz - 6}" x2="${cx + 10}" y2="${sz - 6}"
                stroke="rgba(255,255,255,0.3)" stroke-width="1" stroke-linecap="round"/>
        </g>
    </svg>`;

    return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: { width: Math.round(totalW * 0.84), height: Math.round(totalH * 0.84) },
        anchor: { x: Math.round(totalW * 0.42), y: Math.round((labelH + 4 + sz / 2) * 0.84) },
    };
}

function tocarSom(ligando) {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        // Ligando: melodia subindo (agudo) — 3 notas ascendentes
        // Desligando: melodia descendo (grave) — 3 notas descendentes com fade lento
        const tones = ligando
            ? [
                { freq: 660,  time: 0,    dur: 0.4 },
                { freq: 880,  time: 0.18, dur: 0.4 },
                { freq: 1100, time: 0.36, dur: 0.7 },
              ]
            : [
                { freq: 880,  time: 0,    dur: 0.5 },
                { freq: 660,  time: 0.22, dur: 0.5 },
                { freq: 440,  time: 0.44, dur: 0.9 },
              ];
        tones.forEach(({ freq, time, dur }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.value = freq;
            const start = ctx.currentTime + time;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
            osc.start(start);
            osc.stop(start + dur);
        });
    } catch(e) {}
}

function tempoRelativo(data) {
    if (!data) return 'Sem comunicação';
    const diff = Math.floor((Date.now() - new Date(data)) / 1000);
    if (diff < 60) return `${diff}s atrás`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return formatarData(data);
}

export default function Mapa() {
    const [veiculos, setVeiculos] = useState([]);
    const [selecionado, setSelecionado] = useState(null);
    const [seguindo, setSeguindo] = useState(null); // id do veículo sendo seguido
    const [satelite, setSatelite] = useState(false);
    const [todosPoI, setTodosPoI] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState(null); // 'movimento' | 'parado' | 'deslig.' | null
    const [busca, setBusca] = useState('');
    const [painelAberto, setPainelAberto] = useState(true);
    const mapRef = useRef(null);
    const centralizadoRef = useRef(false);
    const ignicaoAnteriorRef = useRef({});
    const centroInicialRef = useRef(null);

    // Animação suave de posição
    const posAlvoRef    = useRef({}); // posição real vinda da API  { [id]: {lat, lng} }
    const posAtualRef   = useRef({}); // posição interpolada atual   { [id]: {lat, lng} }
    const rafRef        = useRef(null);
    const [posAnimadas, setPosAnimadas] = useState({}); // dispara re-render

    const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY });

    const [carregouInicial, setCarregouInicial] = useState(false);

    // Loop de interpolação (roda enquanto há veículos com posição)
    useEffect(() => {
        const DURACAO_MS = 1800; // duração da transição em milissegundos
        let ultimo = null;
        const tick = (agora) => {
            const dt = ultimo ? Math.min(agora - ultimo, 100) : 16;
            ultimo = agora;
            const fator = Math.min(dt / DURACAO_MS, 1); // fração do frame
            let mudou = false;
            const snap = {};
            Object.keys(posAlvoRef.current).forEach(id => {
                const alvo = posAlvoRef.current[id];
                const atual = posAtualRef.current[id] || alvo;
                const lat = atual.lat + (alvo.lat - atual.lat) * fator * 8;
                const lng = atual.lng + (alvo.lng - atual.lng) * fator * 8;
                const dLat = Math.abs(lat - atual.lat);
                const dLng = Math.abs(lng - atual.lng);
                if (dLat > 1e-8 || dLng > 1e-8) mudou = true;
                posAtualRef.current[id] = { lat, lng };
                snap[id] = { lat, lng };
            });
            if (mudou) setPosAnimadas({ ...snap });
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, []);

    const carregar = async (inicial = false) => {
        try {
            const res = await getVeiculos();
            setVeiculos(res.data);
            // Atualiza alvos de animação
            res.data.forEach(v => {
                if (v.latitude && v.longitude) {
                    posAlvoRef.current[v.id] = { lat: v.latitude, lng: v.longitude };
                    // Inicializa posição atual na primeira vez (sem interpolação)
                    if (!posAtualRef.current[v.id]) {
                        posAtualRef.current[v.id] = { lat: v.latitude, lng: v.longitude };
                    }
                }
            });
            if (inicial) setCarregouInicial(true);
        } catch (e) {
            console.error('Erro ao carregar veículos:', e);
            if (inicial) setCarregouInicial(true);
        }
    };

    useEffect(() => {
        carregar(true);
        const interval = setInterval(() => carregar(false), 10000);
        return () => clearInterval(interval);
    }, []);

    const onMapLoad = useCallback((map) => { mapRef.current = map; }, []); // mantido para referência

    // Centraliza na primeira carga ou segue veículo selecionado
    useEffect(() => {
        if (!mapRef.current || veiculos.length === 0) return;

        if (seguindo) {
            const alvo = veiculos.find(v => v.id === seguindo);
            if (alvo && alvo.latitude && alvo.longitude) {
                mapRef.current.panTo({ lat: alvo.latitude, lng: alvo.longitude });
            }
            return;
        }

        if (centralizadoRef.current) return;
        const comPosicao = veiculos.filter(posicaoParaEnquadrar);
        if (comPosicao.length === 0) return;
        if (comPosicao.length === 1) {
            mapRef.current.panTo({ lat: Number(comPosicao[0].latitude), lng: Number(comPosicao[0].longitude) });
            mapRef.current.setZoom(15);
        } else {
            const bounds = new window.google.maps.LatLngBounds();
            comPosicao.forEach(v => bounds.extend({ lat: Number(v.latitude), lng: Number(v.longitude) }));
            mapRef.current.fitBounds(bounds);
        }
        centralizadoRef.current = true;
    }, [veiculos, seguindo]);

    // Sincroniza selecionado com dados atualizados
    useEffect(() => {
        if (selecionado) {
            const atualizado = veiculos.find(v => v.id === selecionado.id);
            if (atualizado) setSelecionado(atualizado);
        }
    }, [veiculos]);

    // Rastro: array de posições vindas do banco (posicoes_trail), ordenadas do mais antigo ao mais recente
    const trails = React.useMemo(() => {
        const resultado = {};
        veiculos.forEach(v => {
            const { ignicao } = ignicaoEfetiva(v);
            if (!ignicao || !(v.velocidade > 0) || !v.latitude || !v.longitude) return;
            if (Array.isArray(v.posicoes_trail) && v.posicoes_trail.length >= 2) {
                resultado[v.id] = v.posicoes_trail.map(p => ({ lat: p.latitude, lng: p.longitude }));
            }
        });
        return resultado;
    }, [veiculos]);

    // Detecta mudança de ignição e toca sino se habilitado
    // Monitora ignicao_atual (evento direto, sem GPS) e ignicao (da posição GPS)
    useEffect(() => {
        veiculos.forEach(v => {
            const { ignicao: ignicaoReal } = ignicaoEfetiva(v);
            const anterior = ignicaoAnteriorRef.current[v.id];
            if (anterior !== undefined && anterior !== ignicaoReal && v.alerta_sonoro) {
                tocarSom(ignicaoReal === true);
            }
            ignicaoAnteriorRef.current[v.id] = ignicaoReal;
        });
    }, [veiculos]);

    const clicarVeiculo = (v) => {
        setSelecionado(v);
        if (mapRef.current && v.latitude && v.longitude) {
            mapRef.current.panTo({ lat: v.latitude, lng: v.longitude });
            mapRef.current.setZoom(16);
        }
    };

    const toggleSeguir = (id) => {
        setSeguindo(prev => prev === id ? null : id);
    };

    if (!isLoaded || !carregouInicial) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', gap: 16 }}>
            <div style={{ width: 36, height: 36, border: '4px solid #ddd', borderTopColor: '#2980b9', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ fontSize: 15, color: '#555' }}>Carregando mapa...</div>
        </div>
    );

    const comPosicao = veiculos.filter(v => v.latitude && v.longitude);
    const paraEnquadrar = comPosicao.filter(posicaoParaEnquadrar);

    // Centro inicial — calculado só uma vez na primeira carga
    if (!centroInicialRef.current && paraEnquadrar.length > 0) {
        const lats = paraEnquadrar.map(v => Number(v.latitude));
        const lngs = paraEnquadrar.map(v => Number(v.longitude));
        centroInicialRef.current = {
            lat: (Math.min(...lats) + Math.max(...lats)) / 2,
            lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
        };
    }
    const centroCalculado = centroInicialRef.current || centroInicial;

    return (
        <div style={{ display: 'flex', height: 'calc(100vh - 52px)', position: 'relative' }}>

            {/* ── Painel lateral — tema iOS ── */}
            <div style={{
                width: painelAberto ? 272 : 0,
                minWidth: painelAberto ? 272 : 0,
                overflow: 'hidden',
                transition: 'width 0.28s cubic-bezier(0.32,0.72,0,1), min-width 0.28s cubic-bezier(0.32,0.72,0,1)',
                background: 'rgba(242,242,247,0.97)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                display: 'flex', flexDirection: 'column',
                borderRight: '1px solid rgba(0,0,0,0.08)',
                zIndex: 10,
                fontFamily: 'var(--ios-font)',
            }}>
                {/* Cabeçalho */}
                <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(0,0,0,0.07)', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 17, color: '#1c1c1e', letterSpacing: -0.3, marginBottom: 8 }}>
                        Frota
                    </div>
                    {/* Campo de busca */}
                    <div style={{ position: 'relative', marginBottom: 10 }}>
                        <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#aeaeb2" strokeWidth="2.2" strokeLinecap="round">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                            type="text"
                            placeholder="Buscar veículo..."
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            style={{
                                width: '100%', boxSizing: 'border-box',
                                padding: '7px 28px 7px 30px',
                                border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8,
                                fontSize: 13, fontFamily: 'var(--ios-font)', color: '#1c1c1e',
                                background: 'white', outline: 'none',
                            }}
                        />
                        {busca && (
                            <span onClick={() => setBusca('')} style={{
                                position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
                                cursor: 'pointer', color: '#aeaeb2', fontSize: 15, lineHeight: 1,
                            }}>×</span>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 6 }}>
                        {[
                            { cor: '#34c759', label: veiculos.filter(v => ignicaoEfetiva(v).ignicao).length, desc: 'movimento' },
                            { cor: '#ff3b30', label: veiculos.filter(v => !ignicaoEfetiva(v).ignicao).length, desc: 'desligado' },
                        ].map(({ cor, label, desc }) => {
                            const ativo = filtroStatus === desc;
                            return (
                                <div key={desc} onClick={() => setFiltroStatus(ativo ? null : desc)}
                                    style={{
                                        flex: 1, borderRadius: 8, padding: '6px 8px', textAlign: 'center',
                                        cursor: 'pointer', transition: 'all 0.15s',
                                        background: ativo ? cor : 'white',
                                        boxShadow: ativo ? `0 2px 8px ${cor}55` : '0 1px 4px rgba(0,0,0,0.06)',
                                        border: `1.5px solid ${ativo ? cor : 'transparent'}`,
                                    }}>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: ativo ? 'white' : cor }}>{label}</div>
                                    <div style={{ fontSize: 10, color: ativo ? 'rgba(255,255,255,0.85)' : '#8e8e93', marginTop: 1 }}>{desc}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Lista */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {veiculos.filter(v => {
                        const termo = busca.trim().toLowerCase();
                        if (termo && !v.nome.toLowerCase().includes(termo) && !v.placa.toLowerCase().includes(termo)) return false;
                        if (!filtroStatus) return true;
                        const { ignicao } = ignicaoEfetiva(v);
                        if (filtroStatus === 'movimento')  return ignicao;
                        if (filtroStatus === 'desligado')  return !ignicao;
                        return true;
                    }).sort((a, b) => {
                        const igA = ignicaoEfetiva(a).ignicao ? 1 : 0;
                        const igB = ignicaoEfetiva(b).ignicao ? 1 : 0;
                        if (igB !== igA) return igB - igA; // ignição ligada primeiro
                        const tA = a.recebido_em ? new Date(a.recebido_em).getTime() : 0;
                        const tB = b.recebido_em ? new Date(b.recebido_em).getTime() : 0;
                        return tB - tA; // mais recente primeiro dentro de cada grupo
                    }).map((v, i) => {
                        const status = statusVeiculo(v);
                        const isSeguindo = seguindo === v.id;
                        const isSelecionado = selecionado && selecionado.id === v.id;
                        return (
                            <div key={v.id} onClick={() => clicarVeiculo(v)} style={{
                                padding: '11px 16px',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                cursor: 'pointer',
                                background: isSelecionado ? 'rgba(0,122,255,0.07)' : 'transparent',
                                transition: 'background 0.15s',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                    {/* Indicador de status */}
                                    <div style={{
                                        width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                                        background: status.cor,
                                        boxShadow: `0 0 5px ${status.cor}88`,
                                    }}/>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1c1c1e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {v.nome}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#8e8e93', marginTop: 1 }}>{v.placa}</div>
                                    </div>
                                    {/* Botão seguir */}
                                    <button
                                        onClick={e => { e.stopPropagation(); toggleSeguir(v.id); }}
                                        title={isSeguindo ? 'Parar de seguir' : 'Seguir no mapa'}
                                        style={{
                                            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                                            background: isSeguindo ? '#007aff' : 'rgba(0,0,0,0.06)',
                                            border: 'none', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'background 0.2s',
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="3" fill={isSeguindo ? 'white' : '#636366'}/>
                                            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke={isSeguindo ? 'white' : '#636366'} strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    </button>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, alignItems: 'center' }}>
                                    <span style={{ fontSize: 11, fontWeight: 500, color: status.cor }}>
                                        {status.label}
                                        {v.ignicao && v.velocidade > 0 ? ` · ${v.velocidade} km/h` : ''}
                                    </span>
                                    <span style={{ fontSize: 10, color: '#aeaeb2' }}>{tempoRelativo(v.recebido_em)}</span>
                                </div>
                            </div>
                        );
                    })}
                    {veiculos.length === 0 && (
                        <div style={{ color: '#aeaeb2', fontSize: 14, padding: 24, textAlign: 'center' }}>
                            Nenhum veículo cadastrado
                        </div>
                    )}
                    {veiculos.length > 0 && filtroStatus && veiculos.filter(v => {
                        const { ignicao } = ignicaoEfetiva(v);
                        if (filtroStatus === 'movimento')  return ignicao;
                        if (filtroStatus === 'desligado')  return !ignicao;
                        return false;
                    }).length === 0 && (
                        <div style={{ color: '#aeaeb2', fontSize: 13, padding: 24, textAlign: 'center' }}>
                            Nenhum veículo {filtroStatus}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Mapa ── */}
            <div style={{ flex: 1, position: 'relative' }}>

                {/* Botão abrir/fechar painel */}
                <div onClick={() => setPainelAberto(p => !p)} style={{
                    position: 'absolute', top: 12, left: 12, zIndex: 1000,
                    background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    borderRadius: 10, width: 36, height: 36,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.15)', cursor: 'pointer',
                }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3a3a3c" strokeWidth="2.2" strokeLinecap="round">
                        {painelAberto
                            ? <><line x1="18" y1="6" x2="6" y2="12"/><line x1="6" y1="12" x2="18" y2="18"/></>
                            : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
                        }
                    </svg>
                </div>

                {/* Controles superiores direitos */}
                <div style={{
                    position: 'absolute', top: 12, right: 12, zIndex: 1000,
                    display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end',
                }}>
                    {/* Botão satélite */}
                    <div onClick={() => setSatelite(s => !s)} style={{
                        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                        padding: '7px 14px', borderRadius: 10,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.15)', cursor: 'pointer',
                        fontFamily: 'var(--ios-font)', fontSize: 13, fontWeight: 500,
                        color: '#3a3a3c', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            {satelite
                                ? <><polygon points="3,11 22,2 13,21 11,13 3,11"/></>
                                : <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>
                            }
                        </svg>
                        {satelite ? 'Mapa' : 'Satélite'}
                    </div>

                    {/* Checkbox pontos de referência */}
                    <div onClick={() => setTodosPoI(p => !p)} style={{
                        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                        padding: '7px 14px', borderRadius: 10,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.15)', cursor: 'pointer',
                        fontFamily: 'var(--ios-font)', fontSize: 13, fontWeight: 500,
                        color: '#3a3a3c', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <div style={{
                            width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                            background: todosPoI ? '#007aff' : 'white',
                            border: todosPoI ? 'none' : '1.5px solid #c7c7cc',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s',
                        }}>
                            {todosPoI && (
                                <svg width="10" height="8" viewBox="0 0 12 10" fill="none">
                                    <path d="M1 5l3.5 3.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </div>
                        Pontos de referência
                    </div>
                </div>

                {/* Banner "seguindo" */}
                {seguindo && (() => {
                    const alvo = veiculos.find(v => v.id === seguindo);
                    return alvo ? (
                        <div style={{
                            position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                            zIndex: 1000,
                            background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                            color: '#1c1c1e', padding: '9px 18px', borderRadius: 20,
                            fontSize: 13, fontFamily: 'var(--ios-font)', fontWeight: 500,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            display: 'flex', alignItems: 'center', gap: 8,
                            border: '1px solid rgba(0,0,0,0.06)',
                        }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#007aff', boxShadow: '0 0 6px #007aff' }}/>
                            Seguindo <strong>{alvo.nome}</strong>
                            <span onClick={() => setSeguindo(null)} style={{ cursor: 'pointer', color: '#ff3b30', marginLeft: 4, fontSize: 15, lineHeight: 1 }}>×</span>
                        </div>
                    ) : null;
                })()}

                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={centroCalculado}
                    zoom={paraEnquadrar.length === 1 ? 15 : 12}
                    onLoad={(map) => {
                        mapRef.current = map;
                        // Se houver múltiplos veículos, ajusta bounds logo ao carregar
                        if (paraEnquadrar.length > 1) {
                            const bounds = new window.google.maps.LatLngBounds();
                            paraEnquadrar.forEach(v => bounds.extend({ lat: Number(v.latitude), lng: Number(v.longitude) }));
                            map.fitBounds(bounds);
                        }
                        centralizadoRef.current = true;
                    }}
                    mapTypeId={satelite ? 'hybrid' : 'roadmap'}
                    options={{
                        fullscreenControl: false,
                        streetViewControl: false,
                        mapTypeControl: false,
                        zoomControlOptions: { position: 7 },
                        styles: todosPoI ? [] : [
                            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                            { featureType: 'poi.government', stylers: [{ visibility: 'on' }] },
                            { featureType: 'poi.medical', stylers: [{ visibility: 'on' }] },
                            { featureType: 'poi.attraction', stylers: [{ visibility: 'on' }] },
                            { featureType: 'poi.sports_complex', stylers: [{ visibility: 'on' }] },
                            { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                        ],
                    }}
                >
                    {comPosicao.map(v => {
                        const pos = posAnimadas[v.id] || { lat: v.latitude, lng: v.longitude };
                        return (
                            <Marker
                                key={v.id}
                                position={pos}
                                icon={iconeVeiculo(v)}
                                title={v.nome}
                                onClick={() => clicarVeiculo(v)}
                            />
                        );
                    })}

                    {Object.entries(trails).map(([id, path]) => {
                        const posAtual = posAnimadas[id];
                        // Substitui o último ponto (posição atual) pela versão animada
                        const pathFinal = posAtual && path.length >= 1
                            ? [...path.slice(0, -1), posAtual]
                            : path;
                        return pathFinal.length >= 2 ? (
                            <Polyline
                                key={`trail-${id}`}
                                path={pathFinal}
                                options={{
                                    strokeColor: '#2980b9',
                                    strokeOpacity: 0.8,
                                    strokeWeight: 5,
                                    geodesic: true,
                                }}
                            />
                        ) : null;
                    })}

                    {selecionado && selecionado.latitude && (
                        <InfoWindow
                            position={posAnimadas[selecionado.id] || { lat: selecionado.latitude, lng: selecionado.longitude }}
                            onCloseClick={() => setSelecionado(null)}
                        >
                            {(() => {
                                const status = statusVeiculo(selecionado);
                                const InfoRow = ({ icon, label, value }) => (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f2f2f7' }}>
                                        <span style={{ fontSize: 12, color: '#8e8e93' }}>{icon} {label}</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1c1c1e' }}>{value}</span>
                                    </div>
                                );
                                return (
                                    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', minWidth: 220, padding: '2px 0' }}>
                                        <div style={{ marginBottom: 10 }}>
                                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1c1c1e', letterSpacing: -0.3 }}>{selecionado.nome}</div>
                                            <div style={{ fontSize: 12, color: '#8e8e93', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {selecionado.placa}
                                                <span style={{ background: status.cor + '20', color: status.cor, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                                    {status.label}
                                                </span>
                                            </div>
                                        </div>
                                        <InfoRow icon="⚡" label="Velocidade" value={`${selecionado.velocidade || 0} km/h`} />
                                        <InfoRow icon="🔑" label="Ignição" value={selecionado.ignicao ? 'Ligada' : 'Desligada'} />
                                        <InfoRow icon="🔋" label="Bateria" value={selecionado.bateria ? selecionado.bateria.toFixed(1) + ' V' : 'N/D'} />
                                        <InfoRow icon="📡" label="Satélites" value={selecionado.satellites || 0} />
                                        <div style={{ paddingTop: 7, fontSize: 11, color: '#aeaeb2', textAlign: 'right' }}>
                                            {formatarData(selecionado.recebido_em)}
                                        </div>
                                        <button
                                            onClick={() => window.open(`https://www.google.com/maps?q=&layer=c&cbll=${selecionado.latitude},${selecionado.longitude}`, '_blank')}
                                            style={{
                                                marginTop: 10, width: '100%', padding: '8px 0',
                                                background: '#007aff', color: 'white', border: 'none',
                                                borderRadius: 8, fontSize: 13, fontWeight: 600,
                                                cursor: 'pointer', fontFamily: '-apple-system, sans-serif',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                            }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
                                                <circle cx="12" cy="12" r="10"/>
                                                <path d="M12 8v4l3 3"/>
                                            </svg>
                                            Street View
                                        </button>
                                    </div>
                                );
                            })()}
                        </InfoWindow>
                    )}
                </GoogleMap>
            </div>
        </div>
    );
}
